import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Newspaper, PlusCircle, Rss, Pencil, Trash2, LogOut, Eye, Sparkles, ImagePlus, MessageSquare, MessageCircle, Megaphone, X, GripVertical, Eraser } from "lucide-react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortablePhoto({ id, img, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative border-2 bg-white ${index === 0 ? "border-gold" : "border-ink/20"} ${isDragging ? "opacity-50 z-10" : ""}`}
      data-testid={`form-image-item-${index}`}
    >
      <img src={img} alt={`फोटो ${index + 1}`} className="w-28 h-20 object-cover pointer-events-none" />
      {index === 0 && (
        <span className="absolute bottom-0 left-0 bg-gold text-ink text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5">
          मुख्य
        </span>
      )}
      <button
        {...attributes}
        {...listeners}
        data-testid={`form-image-drag-${index}`}
        aria-label="फोटो खींचकर क्रम बदलें"
        className="absolute top-1 left-1 w-5 h-5 bg-ink/70 text-paper rounded-sm flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-deepgreen transition-colors"
      >
        <GripVertical size={11} />
      </button>
      <button
        data-testid={`form-image-remove-${index}`}
        onClick={() => onRemove(index)}
        aria-label="फोटो हटाएं"
        className="absolute -top-2 -right-2 w-5 h-5 bg-brk text-paper rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
      >
        <X size={11} />
      </button>
    </div>
  );
}
import api, { apiError } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { SECTIONS, pickTitle, timeAgo } from "../lib/i18n";
import AdminAds from "../components/AdminAds";

const EMPTY_FORM = {
  title: "",
  title_en: "",
  summary: "",
  summary_en: "",
  body: "",
  body_en: "",
  category: "national",
  kind: "news",
  image_url: "",
  image_caption: "",
  images: [],
  author: "वैनर न्यूज़ ब्यूरो",
  tags: "",
  breaking: false,
  featured: false,
  status: "published",
  scheduled_at: "",
};

const inputCls =
  "w-full border border-ink/25 bg-white px-3 py-2 text-sm outline-none focus:border-deepgreen transition-colors";

function LoginForm() {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) setError(res.error);
    else toast.success("स्वागत है, एडिटर!");
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="admin-login">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white border-2 border-ink shadow-[6px_6px_0_#D4AF37] p-8"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brk">The Vainor India • Editorial Desk</span>
        <h1 className="font-hindi text-3xl mt-1 mb-6">एडमिन लॉगिन</h1>
        <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
          Email
        </label>
        <input
          data-testid="admin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
          required
        />
        <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mt-4 mb-1.5">
          Password
        </label>
        <input
          data-testid="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputCls}
          required
        />
        {error && (
          <p data-testid="admin-login-error" className="text-brk text-xs mt-3 font-mono">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="w-full mt-6 bg-deepgreen text-paper py-2.5 font-semibold hover:bg-deepgreen-hover transition-colors disabled:opacity-60"
        >
          {busy ? "…" : "लॉगिन करें"}
        </button>
      </form>
    </div>
  );
}

function Editor({ form, setForm, onSave, onCancel, saving, editingId, onUpload, uploading, onTranslate, translating, onRemoveImage, onReorderImages }) {
  const fileRef = useRef(null);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const imageIds = form.images.map((img, i) => `${i}-${img}`);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = imageIds.indexOf(active.id);
    const to = imageIds.indexOf(over.id);
    if (from === -1 || to === -1) return;
    onReorderImages(arrayMove(form.images, from, to));
  };
  const onAddImageUrl = () => {
    const u = imageUrlInput.trim();
    if (!u) return;
    if (!form.images.includes(u)) setForm({ ...form, images: [...form.images, u] });
    setImageUrlInput("");
  };
  const saveLabel = editingId
    ? "अपडेट करें"
    : form.status === "draft"
    ? "ड्राफ़्ट सहेजें"
    : form.status === "scheduled"
    ? "शेड्यूल करें"
    : "प्रकाशित करें";
  return (
    <div className="bg-white border-2 border-ink p-6" data-testid="article-editor">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            शीर्षक (हिंदी) *
          </label>
          <input data-testid="form-title" value={form.title} onChange={set("title")} className={inputCls} required />
        </div>
        <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 border border-gold/60 bg-gold-light/20 px-4 py-3">
          <span className="text-xs font-mono uppercase tracking-widest text-inkgray">
            एक भाषा में लिखें, एक क्लिक में दूसरी भाषा का अनुवाद पाएं
          </span>
          <div className="flex gap-2">
            <button
              data-testid="form-translate-en"
              onClick={() => onTranslate("hi-en")}
              disabled={!!translating || !form.title.trim()}
              className="flex items-center gap-2 bg-gold text-ink px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-gold-accent hover:text-paper transition-colors disabled:opacity-60"
            >
              <Sparkles size={14} /> {translating === "hi-en" ? "अनुवाद हो रहा…" : "→ English"}
            </button>
            <button
              data-testid="form-translate-hi"
              onClick={() => onTranslate("en-hi")}
              disabled={!!translating || !form.title_en.trim()}
              className="flex items-center gap-2 bg-deepgreen text-paper px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-deepgreen-hover transition-colors disabled:opacity-60"
            >
              <Sparkles size={14} /> {translating === "en-hi" ? "अनुवाद हो रहा…" : "→ हिंदी"}
            </button>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            Headline (English)
          </label>
          <input data-testid="form-title-en" value={form.title_en} onChange={set("title_en")} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            सारांश (हिंदी)
          </label>
          <textarea data-testid="form-summary" value={form.summary} onChange={set("summary")} rows={2} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            Summary (English)
          </label>
          <textarea data-testid="form-summary-en" value={form.summary_en} onChange={set("summary_en")} rows={2} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            मुख्य लेख (हिंदी) — पैराग्राफ के बीच खाली लाइन
          </label>
          <textarea data-testid="form-body" value={form.body} onChange={set("body")} rows={7} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            Full article (English)
          </label>
          <textarea data-testid="form-body-en" value={form.body_en} onChange={set("body_en")} rows={7} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Section</label>
          <select data-testid="form-category" value={form.category} onChange={set("category")} className={inputCls}>
            {SECTIONS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.hi} / {s.en}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            प्रकार (Content Type)
          </label>
          <select data-testid="form-kind" value={form.kind} onChange={set("kind")} className={inputCls}>
            <option value="news">समाचार</option>
            <option value="video">वीडियो</option>
            <option value="gallery">फोटो गैलरी</option>
            <option value="live">लाइव ब्लॉग</option>
            <option value="opinion">विचार/संपादकीय</option>
            <option value="webstory">वेब स्टोरी</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">लेखक</label>
          <input data-testid="form-author" value={form.author} onChange={set("author")} className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            फोटो (एक से ज़्यादा डाल सकते हैं)
          </label>
          <div className="flex gap-2">
            <input
              data-testid="form-image-url-input"
              value={imageUrlInput}
              onChange={(e) => setImageUrlInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAddImageUrl(); } }}
              className={inputCls}
              placeholder="https://… फोटो का URL डालकर 'जोड़ें' दबाएं"
            />
            <button
              data-testid="form-image-url-add"
              onClick={onAddImageUrl}
              disabled={!imageUrlInput.trim()}
              className="shrink-0 border border-ink/30 px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:border-deepgreen hover:text-deepgreen transition-colors disabled:opacity-60"
            >
              जोड़ें
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            data-testid="form-image-file"
            className="hidden"
            onChange={onUpload}
          />
          <button
            data-testid="form-image-upload"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="mt-2 flex items-center gap-2 border border-ink/30 px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:border-deepgreen hover:text-deepgreen transition-colors disabled:opacity-60"
          >
            <ImagePlus size={14} /> {uploading ? "अपलोड हो रहा…" : "कंप्यूटर से फोटो अपलोड करें (एक साथ कई चुन सकते हैं)"}
          </button>
          {form.images.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={imageIds} strategy={rectSortingStrategy}>
                <div className="flex flex-wrap gap-3 mt-4" data-testid="form-image-list">
                  {form.images.map((img, i) => (
                    <SortablePhoto key={imageIds[i]} id={imageIds[i]} img={img} index={i} onRemove={onRemoveImage} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
          <p className="font-mono text-[10px] text-muted-foreground mt-2">
            पहली फोटो मुख्य फोटो होगी — वही होमपेज और कार्ड पर दिखेगी। क्रम बदलने के लिए फोटो के कोने वाले हैंडल से खींचकर आगे-पीछे करें।
          </p>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">फोटो कैप्शन</label>
          <input data-testid="form-caption" value={form.image_caption} onChange={set("image_caption")} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            Tags (कॉमा से अलग)
          </label>
          <input data-testid="form-tags" value={form.tags} onChange={set("tags")} className={inputCls} placeholder="opinion, politics" />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">स्थिति</label>
          <select data-testid="form-status" value={form.status} onChange={set("status")} className={inputCls}>
            <option value="published">अभी प्रकाशित करें</option>
            <option value="draft">ड्राफ़्ट सहेजें (छिपा रहेगा)</option>
            <option value="scheduled">शेड्यूल करें (समय पर अपने-आप प्रकाशित)</option>
          </select>
        </div>
        {form.status === "scheduled" && (
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
              प्रकाशन तिथि-समय
            </label>
            <input
              type="datetime-local"
              data-testid="form-schedule"
              value={form.scheduled_at}
              onChange={set("scheduled_at")}
              className={inputCls}
            />
          </div>
        )}
        <div className="md:col-span-2 flex flex-wrap items-center gap-8">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="form-breaking" type="checkbox" checked={form.breaking} onChange={set("breaking")} className="w-4 h-4 accent-[#D92525]" />
            ब्रेकिंग (टिकर + WhatsApp अलर्ट)
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="w-4 h-4 accent-[#0F382C]" />
            फीचर्ड (हीरो स्टोरी)
          </label>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <button
          data-testid="form-save"
          onClick={onSave}
          disabled={saving || !form.title.trim() || (form.status === "scheduled" && !form.scheduled_at)}
          className="bg-deepgreen text-paper px-6 py-2.5 font-semibold hover:bg-deepgreen-hover transition-colors disabled:opacity-60"
        >
          {saving ? "…" : saveLabel}
        </button>
        <button
          data-testid="form-cancel"
          onClick={onCancel}
          className="border border-ink/30 px-6 py-2.5 font-semibold hover:border-deepgreen transition-colors"
        >
          रद्द करें
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status, scheduled_at }) {
  if (status === "draft") return <span className="text-gold-accent"> • ड्राफ़्ट</span>;
  if (status === "scheduled")
    return (
      <span className="text-deepgreen">
        {" "}• शेड्यूल्ड {scheduled_at ? `(${new Date(scheduled_at).toLocaleString("hi-IN")})` : ""}
      </span>
    );
  return null;
}

export default function AdminPage() {
  const { admin, logout } = useAdminAuth();
  const [tab, setTab] = useState("list");
  const [articles, setArticles] = useState([]);
  const [comments, setComments] = useState([]);
  const [waSubs, setWaSubs] = useState([]);
  const [waMeta, setWaMeta] = useState({ configured: false, alerts_sent: 0 });
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState("");
  const [rssUrls, setRssUrls] = useState("");
  const [fetching, setFetching] = useState(false);
  const [stockCount, setStockCount] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [rssStatus, setRssStatus] = useState(null);

  const load = () => api.get("/admin/articles").then((r) => setArticles(r.data.articles)).catch(() => {});
  const loadComments = () => api.get("/admin/comments").then((r) => setComments(r.data.comments)).catch(() => {});
  const loadWaSubs = () =>
    api.get("/admin/whatsapp/subscribers").then((r) => {
      setWaSubs(r.data.subscribers);
      setWaMeta({ configured: r.data.configured, alerts_sent: r.data.alerts_sent });
    }).catch(() => {});

  useEffect(() => {
    if (admin) load();
  }, [admin]);

  useEffect(() => {
    if (admin && tab === "comments") loadComments();
    if (admin && tab === "whatsapp") loadWaSubs();
    if (admin && tab === "rss") {
      loadStockCount();
      loadRssStatus();
    }
  }, [admin, tab]);

  if (admin === null) {
    return <div className="py-24 text-center font-mono text-sm text-muted-foreground">…</div>;
  }
  if (admin === false) return <LoginForm />;

  const openEditor = (a) => {
    if (a) {
      setForm({
        title: a.title, title_en: a.title_en, summary: a.summary, summary_en: a.summary_en,
        body: a.body, body_en: a.body_en, category: a.category, kind: a.kind || "news", image_url: a.image_url,
        image_caption: a.image_caption,
        images: a.images && a.images.length ? a.images : (a.image_url ? [a.image_url] : []),
        author: a.author, tags: (a.tags || []).join(", "),
        breaking: a.breaking, featured: a.featured, status: a.status || "published",
        scheduled_at: a.scheduled_at ? new Date(a.scheduled_at).toISOString().slice(0, 16) : "",
      });
      setEditingId(a.id);
    } else {
      setForm(EMPTY_FORM);
      setEditingId(null);
    }
    setTab("editor");
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      image_url: form.images[0] || "",
      tags: form.tags.split(",").map((x) => x.trim()).filter(Boolean),
      scheduled_at: form.status === "scheduled" && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : "",
    };
    try {
      if (editingId) await api.put(`/admin/articles/${editingId}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(
        editingId
          ? "लेख अपडेट हो गया"
          : payload.status === "draft"
          ? "ड्राफ़्ट सहेजा गया"
          : payload.status === "scheduled"
          ? "लेख शेड्यूल हो गया"
          : payload.breaking
          ? "ब्रेकिंग ख़बर प्रकाशित — WhatsApp अलर्ट भेजे जा रहे हैं"
          : "लेख प्रकाशित हो गया"
      );
      setTab("list");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setSaving(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`"${pickTitle(a, "hi")}" हटा दें?`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("लेख हटा दिया गया");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeComment = async (c) => {
    if (!window.confirm("यह टिप्पणी हटा दें?")) return;
    try {
      await api.delete(`/admin/comments/${c.id}`);
      toast.success("टिप्पणी हटा दी गई");
      loadComments();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeSub = async (s) => {
    if (!window.confirm(`${s.phone} को हटा दें?`)) return;
    try {
      await api.delete(`/admin/whatsapp/subscribers/${encodeURIComponent(s.phone)}`);
      toast.success("सब्सक्राइबर हटाया गया");
      loadWaSubs();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const urls = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        urls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      } catch (err) {
        toast.error(apiError(err));
      }
    }
    if (urls.length) {
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      toast.success(urls.length > 1 ? `${urls.length} फोटो अपलोड हो गईं` : "फोटो अपलोड हो गई");
    }
    setUploading(false);
  };

  const removeImage = (i) =>
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }));

  const reorderImages = (imgs) => setForm((prev) => ({ ...prev, images: imgs }));

  const translate = async (direction) => {
    setTranslating(direction);
    try {
      const src =
        direction === "hi-en"
          ? { title: form.title, summary: form.summary, body: form.body }
          : { title: form.title_en, summary: form.summary_en, body: form.body_en };
      const { data } = await api.post("/admin/translate", { ...src, direction });
      if (direction === "hi-en") {
        setForm((p) => ({ ...p, title_en: data.title, summary_en: data.summary, body_en: data.body }));
        toast.success("English अनुवाद तैयार — जाँचकर सहेजें");
      } else {
        setForm((p) => ({ ...p, title: data.title, summary: data.summary, body: data.body }));
        toast.success("हिंदी अनुवाद तैयार — जाँचकर सहेजें");
      }
    } catch (e) {
      toast.error(apiError(e));
    }
    setTranslating("");
  };

  const fetchRss = async () => {
    setFetching(true);
    try {
      const urls = rssUrls.split("\n").map((u) => u.trim()).filter(Boolean);
      const { data } = await api.post("/admin/rss/fetch", { urls });
      toast.success(`RSS फ़ेच पूरा — ${data.imported} नई ख़बरें, ${data.skipped} पहले से मौजूद/पुरानी`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setFetching(false);
  };

  const loadStockCount = () =>
    api.get("/admin/stock-photos/count").then((r) => setStockCount(r.data.count)).catch(() => {});

  const loadRssStatus = () =>
    api.get("/admin/rss/status").then((r) => setRssStatus(r.data)).catch(() => {});

  const cleanupStockPhotos = async () => {
    if (!window.confirm("सभी ख़बरों से system की लगाई गई stock photos हटा दें? असली photos वाली ख़बरें नहीं बदलेंगी।")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/stock-photos/cleanup");
      toast.success(`${data.cleaned} ख़बरों से stock photos हटा दी गईं — अब वे साफ़ text-only दिखेंगी`);
      setStockCount(0);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setCleaning(false);
  };

  const tabs = [
    { id: "list", label: "सभी लेख", icon: Newspaper },
    { id: "editor", label: "नया लेख", icon: PlusCircle },
    { id: "comments", label: "टिप्पणियाँ", icon: MessageSquare },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { id: "ads", label: "विज्ञापन", icon: Megaphone },
    { id: "rss", label: "RSS फ़ेच", icon: Rss },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brk">The Vainor India • Editorial Desk</span>
          <h1 className="font-hindi text-3xl md:text-4xl">एडमिन पैनल</h1>
          <span className="block gold-rule w-24 mt-2" />
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-muted-foreground">{admin.email}</span>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="flex items-center gap-1.5 border border-ink/30 px-3.5 py-2 text-xs font-semibold hover:border-brk hover:text-brk transition-colors"
          >
            <LogOut size={13} /> लॉगआउट
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b-2 border-ink mb-8 overflow-x-auto no-scrollbar">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => (id === "editor" ? openEditor(null) : setTab(id))}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-[2px] whitespace-nowrap transition-colors ${
              tab === id ? "border-brk text-deepgreen" : "border-transparent text-ink hover:text-deepgreen"
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "list" && (
        <div className="bg-white border border-ink/15" data-testid="admin-article-list">
          {articles.map((a) => (
            <div key={a.id} className="flex items-center gap-4 px-5 py-3.5 border-b border-subtle last:border-0">
              <div className="min-w-0 flex-1">
                <p className="font-hindi text-sm md:text-base truncate">{a.title}</p>
                <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                  {a.category} • {a.source} • {a.views} views • {a.likes || 0} likes
                  {(a.images?.length || 0) > 1 && <span className="text-deepgreen"> • {a.images.length} फोटो</span>}
                  {a.breaking && <span className="text-brk"> • ब्रेकिंग</span>}
                  {a.featured && <span className="text-gold-accent"> • फीचर्ड</span>}
                  <StatusBadge status={a.status} scheduled_at={a.scheduled_at} />
                </p>
              </div>
              <a
                href={`/article/${a.id}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`admin-view-${a.id}`}
                className="p-2 text-muted-foreground hover:text-deepgreen transition-colors"
                aria-label="view"
              >
                <Eye size={16} />
              </a>
              <button
                data-testid={`admin-edit-${a.id}`}
                onClick={() => openEditor(a)}
                className="p-2 text-muted-foreground hover:text-deepgreen transition-colors"
                aria-label="edit"
              >
                <Pencil size={16} />
              </button>
              <button
                data-testid={`admin-delete-${a.id}`}
                onClick={() => remove(a)}
                className="p-2 text-muted-foreground hover:text-brk transition-colors"
                aria-label="delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {articles.length === 0 && (
            <p className="p-10 text-center text-sm text-muted-foreground">कोई लेख नहीं</p>
          )}
        </div>
      )}

      {tab === "editor" && (
        <Editor
          form={form}
          setForm={setForm}
          onSave={save}
          onCancel={() => setTab("list")}
          saving={saving}
          editingId={editingId}
          onUpload={uploadPhoto}
          uploading={uploading}
          onTranslate={translate}
          translating={translating}
          onRemoveImage={removeImage}
          onReorderImages={reorderImages}
        />
      )}

      {tab === "comments" && (
        <div className="bg-white border border-ink/15" data-testid="admin-comments-list">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-4 px-5 py-4 border-b border-subtle last:border-0">
              <span className="w-9 h-9 shrink-0 rounded-full bg-deepgreen text-gold-light flex items-center justify-center font-hindi text-sm">
                {c.name.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{c.name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{timeAgo(c.created_at, "hi")}</span>
                </div>
                <p className="text-sm text-inkgray leading-relaxed mt-0.5">{c.text}</p>
                <p className="font-mono text-[10px] text-muted-foreground mt-1 truncate">
                  लेख: {c.article_title}
                </p>
              </div>
              <button
                data-testid={`comment-delete-${c.id}`}
                onClick={() => removeComment(c)}
                className="p-2 text-muted-foreground hover:text-brk transition-colors"
                aria-label="delete comment"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {comments.length === 0 && (
            <p className="p-10 text-center text-sm text-muted-foreground" data-testid="comments-empty">
              अभी कोई टिप्पणी नहीं
            </p>
          )}
        </div>
      )}

      {tab === "whatsapp" && (
        <div className="max-w-2xl space-y-6" data-testid="whatsapp-panel">
          <div className={`border-2 p-5 ${waMeta.configured ? "border-deepgreen bg-white" : "border-gold bg-gold-light/20"}`}>
            <p className="font-semibold text-sm" data-testid="whatsapp-status">
              {waMeta.configured
                ? "WhatsApp अलर्ट चालू हैं — हर ब्रेकिंग ख़बर सब्सक्राइबर्स को अपने-आप भेजी जाएगी।"
                : "WhatsApp सेटअप बाकी है — Twilio keys जुड़ते ही अलर्ट अपने-आप चलने लगेंगे।"}
            </p>
            <p className="font-mono text-xs text-muted-foreground mt-2">
              अब तक भेजे गए अलर्ट: {waMeta.alerts_sent}
            </p>
          </div>
          <div className="bg-white border border-ink/15" data-testid="whatsapp-subscriber-list">
            <h3 className="px-5 py-3 border-b border-ink/10 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              सब्सक्राइबर्स ({waSubs.filter((s) => s.enabled).length})
            </h3>
            {waSubs.map((s) => (
              <div key={s.phone} className="flex items-center gap-4 px-5 py-3.5 border-b border-subtle last:border-0">
                <span className="w-9 h-9 shrink-0 rounded-full bg-deepgreen text-gold-light flex items-center justify-center">
                  <MessageCircle size={15} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm">{s.phone}</p>
                  <p className="font-mono text-[10px] text-muted-foreground">
                    {s.enabled ? "अलर्ट चालू" : "बंद"} • {timeAgo(s.consented_at, "hi")}
                  </p>
                </div>
                <button
                  data-testid={`wa-sub-delete-${s.phone}`}
                  onClick={() => removeSub(s)}
                  className="p-2 text-muted-foreground hover:text-brk transition-colors"
                  aria-label="remove subscriber"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {waSubs.length === 0 && (
              <p className="p-10 text-center text-sm text-muted-foreground">
                अभी कोई सब्सक्राइबर नहीं — readers साइट के footer से अपना नंबर डाल सकते हैं
              </p>
            )}
          </div>
        </div>
      )}

      {tab === "ads" && <AdminAds />}

      {tab === "rss" && (
        <div className="bg-white border-2 border-ink p-6 max-w-2xl" data-testid="rss-panel">
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            RSS feed URL डालें (हर लाइन में एक)। खाली छोड़ने पर डिफ़ॉल्ट फ़ीड्स (NDTV, The Hindu, BBC Hindi) से
            ख़बरें अपने-आप आएंगी। केवल पिछले 48 घंटों की ताज़ा ख़बरें आती हैं; डुप्लिकेट स्किप होती हैं।
            यह काम हर 3 घंटे में अपने-आप भी होता है।
          </p>
          <textarea
            data-testid="rss-urls"
            value={rssUrls}
            onChange={(e) => setRssUrls(e.target.value)}
            rows={5}
            placeholder={"https://feeds.feedburner.com/ndtvnews-top-stories\nhttps://feeds.bbci.co.uk/hindi/rss.xml"}
            className={inputCls}
          />
          <button
            data-testid="rss-fetch-button"
            onClick={fetchRss}
            disabled={fetching}
            className="mt-4 flex items-center gap-2 bg-deepgreen text-paper px-6 py-2.5 font-semibold hover:bg-deepgreen-hover transition-colors disabled:opacity-60"
          >
            <Rss size={16} /> {fetching ? "फ़ेच हो रहा…" : "ख़बरें फ़ेच करें"}
          </button>
          <div className="mt-6 border-t border-ink/10 pt-4" data-testid="rss-status">
            <p className="font-mono text-xs text-muted-foreground">
              अंतिम फ़ेच:{" "}
              {rssStatus?.last_fetch
                ? new Date(rssStatus.last_fetch).toLocaleString("hi-IN", { timeZone: "Asia/Kolkata" }) + " IST"
                : "अभी तक कोई फ़etch नहीं"}
              {" • "}ऑटो-फ़ेच हर 15 मिनट में चलता है
            </p>
            {(rssStatus?.logs || []).slice(0, 5).map((l, i) => (
              <p key={i} className="font-mono text-[11px] text-muted-foreground mt-1.5" data-testid={`rss-log-${i}`}>
                {new Date(l.at).toLocaleString("hi-IN", { timeZone: "Asia/Kolkata" })} — {l.imported} नई, {l.skipped} स्किप
                {l.errors?.length > 0 && <span className="text-brk"> • {l.errors.length} error: {l.errors[0].slice(0, 80)}</span>}
              </p>
            ))}
          </div>
        </div>
      )}

      {tab === "rss" && (
        <div className="bg-white border-2 border-ink p-6 max-w-2xl mt-6" data-testid="stock-cleanup-panel">
          <h3 className="font-hindi text-xl">पुरानी stock photos साफ़ करें</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mt-2">
            पहले system ने जिन RSS ख़बरों में photo नहीं थी उनमें अपनी तरफ़ से एक-ही stock photo लगा दी थी —
            इसीलिए कई अलग ख़बरों पर एक जैसी तस्वीर दिखती है। नीचे वाला button उन सबसे stock photo हटा देगा,
            ताकि वे ख़बरें साफ़ text-only दिखें। असली photo वाली ख़बरें बिल्कुल नहीं बदलेंगी।
          </p>
          <p className="font-mono text-xs text-muted-foreground mt-3" data-testid="stock-photo-count">
            {stockCount === null ? "गिनती जाँची जा रही…" : `${stockCount} ख़बरों में stock photo लगी है`}
          </p>
          <button
            data-testid="stock-cleanup-button"
            onClick={cleanupStockPhotos}
            disabled={cleaning || !stockCount}
            className="mt-4 flex items-center gap-2 bg-brk text-paper px-6 py-2.5 font-semibold hover:opacity-85 transition-opacity disabled:opacity-50"
          >
            <Eraser size={16} /> {cleaning ? "साफ़ हो रहा…" : "Stock photos हटाएं"}
          </button>
        </div>
      )}
    </div>
  );
}
